import { createSelector } from "@reduxjs/toolkit";
import { getMedallionCounts } from "libs/character";
import { getUnlockedUpgrades } from "libs/item";
import { getCharacterSkills } from "libs/skill";
import {
  calculateCurrentBaseStats,
  calculateTotalBaseStats,
  getDerivedStatRangeSets,
  getDerivedStatSets,
  getFilteredBattleRatings,
} from "libs/stats";
import {
  BattleRatingsData,
  CharacterBaseStats,
  DerivedStatSets,
  ExpandedMedallionsData,
  InstalledUpgradesData,
  PilotTypes,
  Planets,
  PlayerSettings,
  RootState,
  ShipTypes,
  SkillModel,
  StatRangeSets,
} from "types";

// USER

export const getUserInfo = createSelector(
  (state: RootState) => state.character.userId,
  (state: RootState) => state.character.userName,
  (userId, userName) => {
    return {
      userId,
      userName,
    };
  }
);

export const getUserSettings = (state: RootState): PlayerSettings =>
  state.character.data.settings;

// CHARACTER

export const getBuildInfo = createSelector(
  (state: RootState) => state.character.data.ship,
  (state: RootState) => state.character.data.pilot,
  (ship, pilot): { ship: ShipTypes; pilot: PilotTypes } => {
    return {
      ship,
      pilot,
    };
  }
);

export const getBattleRatings = createSelector(
  (state: RootState) => state.character.data.battleRatings,
  (battleRatings): BattleRatingsData => {
    // Loop through mobs list to make sure it only incldues active mobs

    const filteredBattleRatings = getFilteredBattleRatings(battleRatings);

    return filteredBattleRatings;
  }
);

export const getUpgrades = createSelector(
  (state: RootState) => state.character.data.ship,
  getBattleRatings,
  (ship, battleRatings): string[] => {
    const upgrades = getUnlockedUpgrades(ship, battleRatings);
    return upgrades;
  }
);

export const getStorageUpgrades = (state: RootState): string[] =>
  state.character.data.storageUpgrades;

// STATS

export const getTotalBaseStats = createSelector(
  (state: RootState) => state.character.data.ship,
  (state: RootState) => state.character.data.enhancedBaseStats,
  (ship, enhancedBaseStats): CharacterBaseStats => {
    const totalBaseStats = calculateTotalBaseStats(ship, enhancedBaseStats);

    return totalBaseStats;
  }
);

export const getEnhancedBaseStats = (state: RootState): CharacterBaseStats =>
  state.character.data.enhancedBaseStats;

export const getWeakenedBaseStats = (state: RootState): CharacterBaseStats =>
  state.character.data.weakenedBaseStats;

export const getCurrentBaseStats = createSelector(
  getTotalBaseStats,
  getWeakenedBaseStats,
  (totalBaseStats, weakenedBaseStats): CharacterBaseStats => {
    const currentBaseStats = calculateCurrentBaseStats(
      totalBaseStats,
      weakenedBaseStats
    );

    return currentBaseStats;
  }
);

export const getDerivedStats = createSelector(
  getCurrentBaseStats,
  getTotalBaseStats,
  (state: RootState) => state.character.data.installedUpgrades,
  (currentBaseStats, totalBaseStats, installedUpgrades): DerivedStatSets => {
    const derivedStats = getDerivedStatSets(
      currentBaseStats,
      totalBaseStats,
      installedUpgrades
    );

    return derivedStats;
  }
);

export const getDerivedStatRanges = createSelector(
  getDerivedStats,
  (derivedStats): StatRangeSets => {
    const derivedStatRanges = getDerivedStatRangeSets(derivedStats);

    return derivedStatRanges;
  }
);

// SHIP

export const getShipHealth = (state: RootState): number =>
  state.character.data.health;

export const getShipInteriorInfo = createSelector(
  (state: RootState) => state.character.data.ui.isInsideShip,
  (state: RootState) => state.character.data.ui.dataScreen,
  (state: RootState) => state.character.userName,
  (
    isInsideShip,
    dataScreen
  ): {
    isInsideShip: boolean;
    dataScreen: string | null;
  } => {
    return {
      isInsideShip,
      dataScreen,
    };
  }
);

export const getShipDisplayInfo = createSelector(
  (state: RootState) => state.character.data.ui.animations,
  (state: RootState) => state.character.data.ui.isMoving,
  (state: RootState) => state.character.data.ui.lastMove,
  (state: RootState) => state.character.data.ui.isMovementDisabled,
  (
    animations,
    isMoving,
    lastMove,
    isMovementDisabled
  ): {
    animations: string[];
    isMoving: boolean;
    lastMove: string;
    isMovementDisabled: boolean;
  } => {
    return {
      animations,
      isMoving,
      lastMove,
      isMovementDisabled,
    };
  }
);

export const getInstalledUpgrades = (state: RootState): InstalledUpgradesData =>
  state.character.data.installedUpgrades;

// PILOT / SKILLS

export const getPilotSkills = createSelector(
  (state: RootState) => state.character.data.pilot,
  (state: RootState) => state.character.data.trainedSkills,
  getDerivedStats,
  (pilot, trainedSkills, derivedStats): SkillModel[] => {
    const pilotSkills = getCharacterSkills(
      pilot,
      trainedSkills,
      derivedStats.current.complete.fasterRecharge
    );

    return pilotSkills;
  }
);

// MEDALLIONS

export const getMedallions = createSelector(
  getBattleRatings,
  (state: RootState) => state.character.data.ship,
  (state: RootState) => state.character.data.enhancedBaseStats,
  getPilotSkills,
  (
    battleRatings,
    ship,
    enhancedBaseStats,
    pilotSkills
  ): ExpandedMedallionsData => {
    const medallions = getMedallionCounts(
      battleRatings,
      ship,
      enhancedBaseStats,
      pilotSkills
    );

    return medallions;
  }
);

// LOCATION

export const getPosition = (state: RootState): number =>
  state.character.data.location.position;

export const getLocationInfo = createSelector(
  (state: RootState) => state.character.data.location.planet,
  (state: RootState) => state.character.data.location.area,
  (state: RootState) => state.character.data.location.isNearBoundary,
  (state: RootState) => state.character.data.ui.isLinkSectorsOverlayOpen,
  (
    planet,
    area,
    isNearBoundary,
    isLinkSectorsOverlayOpen
  ): {
    planet: Planets;
    area: string;
    isNearBoundary: boolean;
    isLinkSectorsOverlayOpen: boolean;
  } => {
    return {
      planet,
      area,
      isNearBoundary,
      isLinkSectorsOverlayOpen,
    };
  }
);

export const getBuildingInfo = createSelector(
  (state: RootState) => state.character.data.location.building,
  (state: RootState) => state.character.data.location.buildingScreen,
  (state: RootState) => state.character.data.location.buildingDialog,
  (
    building,
    buildingScreen,
    buildingDialog
  ): {
    building: string | null;
    buildingScreen: string | null;
    buildingDialog: string | null;
  } => {
    return {
      building,
      buildingScreen,
      buildingDialog,
    };
  }
);

// TRAVELING

export const getTravelInfo = createSelector(
  (state: RootState) => state.character.data.ui.isTravelMode,
  (state: RootState) => state.character.data.location.planetPasses,
  (
    isTravelMode,
    planetPasses
  ): {
    isTravelMode: boolean;
    planetPasses: string[];
  } => {
    return {
      isTravelMode,
      planetPasses,
    };
  }
);
